import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useMovieContext } from "../contexts/MovieContext";
import HeroCarousel from "../components/HeroCarousel";
import MovieCarousel from "../components/MovieCarousel";
import HomeMovieCard from "../components/HomeMovieCard";
import "../css/Home.css";
import "../css/HomeMovieCard.css";
import SEOHome from "../components/SEOHome";

const GENRES = [ "Comedy", "Western", "Thriller", "Horror", "Action", "Sci-Fi", "Drama", "Romance" ];

const CREATIVE_GENRE_TITLES = {
  "Horror": { title: "Spooky Scenes & Midnight Screams", subtitle: "More Spine-Chilling Vaults" },
  "Western": { title: "Gunslingers & Dusty Trails", subtitle: "More Outlaw Tales" },
  "Sci-Fi": { title: "Retro Intergalactic Oddities", subtitle: "More Cosmic Visions" },
  "Comedy": { title: "Vintage Screwball", subtitle: "More Classic Laughs" },
  "Drama": { title: "Golden Era Melodramas", subtitle: "More Dramatic Gems" },
  "Action": { title: "Pulp Action & High Stakes", subtitle: "More Thrills & Spills" },
  "Romance": { title: "Old Hollywood Romance", subtitle: "More Swoon-Worthy Classics" },
  "Thriller": { title: "Hitchcockian Suspense", subtitle: "More Shadowy Mysteries" }
};

const tidy = (s = "") => s.trim().toLowerCase()
  .replace(/\b\w/g, c => c.toUpperCase())
  .replace("Sci-fi", "Sci-Fi");

export default function Home() {
  const {
    movies,
    featuredMovies,
    searchTerm,
    handleSearch,
    loading,
    error,
    filteredMovies,
    popularMovies,
    recentlyAdded,
    progress,
  } = useMovieContext();

  const continueWatching = useMemo(
    () => movies.filter(m => (progress[m.id] ?? 0) > 0).slice(0, 12),
    [movies, progress]
  );

  const heroMovies = useMemo(
    () => movies.filter(m => m.is_hero),
    [movies]
  );

  // Dynamic month-based heading for Popular Movies (e.g., "September's Top 30")
  const popularSectionTitle = useMemo(() => {
    const currentMonth = new Date().toLocaleString("en-US", { month: "long" });
    const count = popularMovies?.length || 30;
    return `${currentMonth}'s Top ${count}`;
  }, [popularMovies]);

  const [grouped, setGrouped] = useState({});
  const [curatorPicks, setCuratorPicks] = useState([]);

  useEffect(() => {
    if (!Array.isArray(movies)) return;

    const genreFeatured = movies.filter(m => m.genre_featured);
    const g = {};

    GENRES.forEach(name => {
      const genre = tidy(name);

      const all = genreFeatured
        .filter(m => tidy(m.genre) === genre)
        .sort((a, b) => (a.genre_order ?? 0) - (b.genre_order ?? 0));

      if (!all.length) return;

      const top = all.slice(0, 12);
      const other = all.slice(12);

      g[genre] = { top, other };
    });

    setGrouped(g);
    setCuratorPicks([]);
  }, [movies]);

  return (
    <>
      <SEOHome />
      <div className="home-container">
        <div className="hero-full-bleed">
          <HeroCarousel movies={heroMovies} searchQuery={searchTerm} handleSearch={handleSearch} />
        </div>

        {loading && <div className="loading-message">Loading…</div>}
        {error && <div className="error-message">{error}</div>}

        {!loading && !error && searchTerm && (
          <section className="search-results-section">
            <h2 className="search-results-title">Search Results</h2>
            {filteredMovies.length ? (
              <div className="movies-grid">
                {filteredMovies.map(m => (
                  <HomeMovieCard key={m.id} movie={m} />
                ))}
              </div>
            ) : (
              <div className="no-results-message">No matches found.</div>
            )}
          </section>
        )}

        {!loading && !error && !searchTerm && (
          <div className="carousel-section">
            {continueWatching.length > 0 && (
              <MovieCarousel title="Continue Watching" movies={continueWatching} />
            )}

            <MovieCarousel title="Featured" movies={featuredMovies} />
            
            {/* 1. Dynamic month & count title */}
            <MovieCarousel title={popularSectionTitle} movies={popularMovies} />

            {Object.entries(grouped).map(([genre, { top, other }]) => {
              const textLookup = Object.entries(CREATIVE_GENRE_TITLES).find(
                ([key]) => tidy(key) === tidy(genre)
              );
              const display = textLookup ? textLookup[1] : { title: `Top ${genre} Movies`, subtitle: `Other ${genre} Movies` };

              return (
                <section key={genre} className="genre-section">
                  <Link to={`/genre/${genre}`} className="genre-link">
                    <h2 className="movie-carousel-title">{display.title}</h2>
                  </Link>
                  <MovieCarousel title="" movies={top} />

                  {other.length >= 6 && (
                    <>
                      <Link to={`/genre/${genre}`} className="genre-link">
                        <h3 className="movie-carousel-subtitle">{display.subtitle}</h3>
                      </Link>
                      <MovieCarousel title="" movies={other} />
                    </>
                  )}
                </section>
              );
            })}

            {/* 2. Custom Title for Recently Added */}
            <MovieCarousel title="Fresh Out of the Vault" movies={recentlyAdded} />

            {/* 3. Custom Title for Curator's Picks */}
            {curatorPicks.length > 0 && (
              <>
                <MovieCarousel title="Our Cinematic Top Picks" movies={curatorPicks.slice(0, 18)} />
                {curatorPicks.length > 18 && (
                  <MovieCarousel title="More Cinematic Top Picks" movies={curatorPicks.slice(18, 36)} />
                )}
              </>
            )}
          </div>
        )}
      </div>
    </>
  );
}